"use client";

import { Card, CardBody, CardHeader, Chip, Progress } from "@heroui/react";
import { IconBriefcase2, IconClipboard } from "@tabler/icons-react";

interface Projetos {
  descricao: string;
  habilitado: boolean;
  id_projeto: string;
  id_usuario: string;
  imagem_atividade: string;
  tags: string[];
  texto: string;
  titulo: string;
  participando: boolean;
  percentual_conclusao: number;
  data_inicio: string;
  data_fim: string;
  status: string;
  meu_status: string;
}

interface ProjectListProps {
  lista: Projetos[];
  onOpenViewProject: (id_projeto: string) => void;
}

const getTotalSemanas = (dataInicio: string, dataFim: string) => {
  const inicio = new Date(dataInicio).getTime();
  const fim = new Date(dataFim).getTime();

  const diffMs = fim - inicio;

  if (diffMs <= 0) return 1;

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 7));
};

const getSemanaAtual = (dataInicio: string, dataFim: string, percentualConclusao: number) => {
  const totalSemanas = getTotalSemanas(dataInicio, dataFim);

  const progresso = Math.min(Math.max(percentualConclusao, 0), 100);

  let semana = Math.floor((progresso / 100) * totalSemanas) + 1;

  if (semana < 1) semana = 1;
  if (semana > totalSemanas) semana = totalSemanas;

  return semana;
};

const progressBarProjetosColors = (percentualConclusao: number) => {
  if (percentualConclusao < 50) {
    return "success" as const;
  } else if (percentualConclusao > 50 && percentualConclusao < 75) {
    return "warning" as const;
  } else if (percentualConclusao > 75) return "danger" as const;

  return "default" as const;
};

export default function ProjectsList({ lista, onOpenViewProject }: ProjectListProps) {
  return (
    <>
      {lista.map((projeto, id) => (
        <button key={projeto.id_projeto ?? id} type="button" onClick={() => onOpenViewProject(projeto.id_projeto)} className="cursor-pointer">
          <Card className="w-full p-5" onPress={() => onOpenViewProject(projeto.id_projeto)}>
            <CardHeader className="flex flex-row justify-between items-start">
              <div className="flex flex-col items-start gap-1">
                <h1 className="text-2xl md:text-3xl font-bold">{projeto.titulo}</h1>
                <div className="text-gray-600 text-sm md:text-base flex flex-row gap-2 justify-center items-center">
                  <div className="rounded-full bg-emerald-500/10 p-1 flex items-center justify-center">
                    <IconClipboard size={18} className="text-emerald-500" />
                  </div>
                  {projeto.descricao}
                </div>
              </div>
              <Chip className="text-[10px] md:text-xs bg-default-100 text-default-600">{projeto.meu_status || projeto.status || "SEM STATUS"}</Chip>
            </CardHeader>

            <div className="flex flex-col gap-3 w-full">
              <div className="text-left rounded-xl p-3 md:p-4 flex flex-col gap-2 hover:bg-default-50 transition-colors cursor-pointer w-full">
                {/* Progresso */}
                <div className="flex flex-col gap-1 mt-1">
                  <div className="flex items-center justify-between">
                    <p className="font-light">Semana {getSemanaAtual(projeto.data_inicio, projeto.data_fim, projeto.percentual_conclusao)}</p>
                    <p className="font-light">{projeto.percentual_conclusao.toFixed()}/100</p>
                  </div>

                  <Progress size="sm" color={progressBarProjetosColors(projeto.percentual_conclusao)} value={projeto.percentual_conclusao} />
                </div>

                {/* Tags */}
                {projeto.tags && projeto.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {projeto.tags.map((tag, id) => (
                      <Chip key={id} className="text-[13px] bg-default-200 text-default-600">
                        #{tag}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </button>
      ))}
    </>
  );
}
